import { FC } from 'react';
import classNames from 'classnames';
import { $convertToMarkdownString } from '@lexical/markdown';
import { MARKDOWN_TRANSFORMERS } from '../../components/Editor/plugins/MarkdownTransformers';
import { parseMarkdown } from '../../lib/markdown';
import {
  Control,
  Controller,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';

import {
  AssessmentAnswer,
  AssessmentQuestionType,
  FreeTextAssessmentQuestion,
  RichTextAssessmentQuestion,
  SignatureAssessmentQuestion,
  MultiSelectAssessmentQuestion,
  MultiSignatureAssessmentQuestion,
  MultipleChoiceAssessmentQuestion,
  ProgramAssessmentSignatureUserDataFragment,
  InitialsAssessmentQuestion,
  ProviderSignatureAssessmentQuestion,
  FileUploadAssessmentQuestion,
  PatientContactAssessmentAnswer,
} from '../../../generated/graphql';

import {
  isFreeTextAssessmentAnswer,
  isRichTextAssessmentAnswer,
  isSignatureAssessmentAnswer,
  isMultiSelectAssessmentAnswer,
  isMultipleChoiceAssessmentAnswer,
  isInitialsAssessmentAnswer,
  isAutofillAssessmentQuestion,
  isDatePickerAssessmentQuestion,
  isPatientContactAssessmentQuestion,
} from '../../lib/assessments';

import markdownToTxt from '../../lib/markdown-to-txt';
import { ProgramAssessmentQuestion } from '../../lib/program-assessment';

import SignatureFlagName from '../../svgs/SignatureFlagName';

import Editor from '../../components/Editor';
import Checkbox from '../../components/Checkbox';
import InputGroup from '../../components/InputGroup';
import CompletedSignature from '../../components/CompletedSignature';

import { formatPatientContactSnapshotAnswer } from '../../lib/patientContactLabels';
import { CompleteProviderFormData } from './helpers';
import MultiSignatureSection from './MultiSignatureSection';
import { ProviderFileUploadForAssessment } from './ProviderFileUploadForAssessment';
import AutofillQuestion from '../ClientPortal/CompleteProgramAssessment/ProgramAssessmentForm/Question/AutofillQuestion';
import DatePickerQuestion from '../ClientPortal/CompleteProgramAssessment/ProgramAssessmentForm/Question/DatePickerQuestion';

const QuestionField: FC<{
  question: string;
  isStatementQuestion?: boolean;
}> = ({ question, isStatementQuestion = false }) => {
  return (
    <div
      className={classNames(
        'text-body',
        !isStatementQuestion && 'mb-4 border-b border-neutral-75 pb-1',
        isStatementQuestion && 'mt-4',
      )}
    >
      <div
        className="prose prose-sm max-w-none text-green-150"
        dangerouslySetInnerHTML={{ __html: parseMarkdown(question) }}
      />
    </div>
  );
};

const Question: FC<{
  question: ProgramAssessmentQuestion;
  answer?: AssessmentAnswer;
  idx: number;
  register: UseFormRegister<CompleteProviderFormData>;
  control: Control<CompleteProviderFormData, unknown>;
  setValue: UseFormSetValue<CompleteProviderFormData>;
  programAssessmentSignatureUsers: ProgramAssessmentSignatureUserDataFragment[];
  currentSigner: ProgramAssessmentSignatureUserDataFragment;
  signatureName: string;
  signatureRole: string;
  signatureTermsAgreed: boolean;
  nowFormattedTimestamp: string;
  formIsReadOnly: boolean;
  fieldPrefix?: string; // Optional prefix for nested forms (e.g., "forms.assessmentId")
  programId?: string; // For practitioner-only form file upload (uploads to client Documents)
}> = ({
  question,
  answer,
  idx,
  register,
  control,
  setValue,
  programAssessmentSignatureUsers,
  currentSigner,
  signatureName,
  signatureRole,
  signatureTermsAgreed,
  nowFormattedTimestamp,
  formIsReadOnly,
  fieldPrefix = '',
  programId,
}) => {
  const getFieldName = (field: string) => {
    const baseName = `answers.${idx}.${field}`;
    return fieldPrefix ? `${fieldPrefix}.${baseName}` : baseName;
  };
  // API may return questionType (camelCase) or question_type (snake_case)
  const questionType =
    question.questionType ??
    (question as { question_type?: string }).question_type;
  switch (questionType) {
    case AssessmentQuestionType.Statement:
      question = question as FreeTextAssessmentQuestion;
      return <QuestionField question={question.question} isStatementQuestion />;

    case AssessmentQuestionType.FreeText:
      question = question as FreeTextAssessmentQuestion;
      return (
        <div>
          <QuestionField question={question.question} />
          <InputGroup
            key={markdownToTxt(question.question)}
            label={question.question}
            defaultValue={
              answer && isFreeTextAssessmentAnswer(answer) ? answer.answer : ''
            }
            {...register(getFieldName('value') as any, {
              required: {
                value: question.isRequired,
                message: 'Please provide a response',
              },
            })}
            inputSize="extra-small"
            className="max-w-[398px]"
            labelHidden
          />
        </div>
      );

    case AssessmentQuestionType.RichText:
      question = question as RichTextAssessmentQuestion;
      return (
        <div>
          <QuestionField question={question.question} />
          <Controller
            control={control}
            name={getFieldName('value') as any}
            defaultValue={
              answer && isRichTextAssessmentAnswer(answer) ? answer.answer : ''
            }
            rules={{
              required: {
                value: question.isRequired,
                message: 'Please provide a response',
              },
            }}
            render={({ field }) => {
              const { onChange, value } = field;
              return (
                <Editor
                  placeholder="Type your response..."
                  className="text-neutral-150"
                  initialContentMarkdown={value}
                  allowLinks={false}
                  onChange={(editorState) => {
                    editorState.read(() => {
                      onChange(
                        $convertToMarkdownString(
                          MARKDOWN_TRANSFORMERS,
                          undefined,
                          true,
                        ),
                      );
                    });
                  }}
                  onError={(error: Error) => {
                    console.error(error);
                  }}
                  readOnly={formIsReadOnly}
                />
              );
            }}
          />
        </div>
      );

    case AssessmentQuestionType.MultipleChoice:
      const multipleChoiceQuestion =
        question as MultipleChoiceAssessmentQuestion;
      const answerOptions = multipleChoiceQuestion.answerOptions;

      return (
        <div>
          <QuestionField question={multipleChoiceQuestion.question} />
          <Controller
            name={getFieldName('value') as any}
            control={control}
            rules={{
              required: {
                value: multipleChoiceQuestion.isRequired,
                message: 'Please choose one of the options',
              },
            }}
            defaultValue={
              answer && isMultipleChoiceAssessmentAnswer(answer)
                ? answer.answer
                : undefined
            }
            render={({ field: { onChange, value } }) => (
              <>
                {answerOptions.map(({ answerOption }, index) => (
                  <Checkbox
                    key={index}
                    id={`answer-${idx}-${index}`}
                    type="radio"
                    className="mb-2"
                    value={answerOption}
                    checked={value === answerOption}
                    readOnly={formIsReadOnly}
                    onChange={() => onChange(answerOption)}
                    labelContent={
                      <div className="ml-2 text-caption">{answerOption}</div>
                    }
                  />
                ))}
              </>
            )}
          />
        </div>
      );

    case AssessmentQuestionType.MultiSelect:
      question = question as MultiSelectAssessmentQuestion;

      return (
        <div>
          <QuestionField question={question.question} />
          {question.answerOptions.map(({ answerOption }, index) => {
            return (
              <div key={index} className="mb-2">
                <Controller
                  name={getFieldName('values') as any}
                  control={control}
                  rules={{
                    required: {
                      value: question.isRequired,
                      message: 'Please choose one at least one option',
                    },
                  }}
                  defaultValue={
                    answer && isMultiSelectAssessmentAnswer(answer)
                      ? answer.answers
                      : []
                  }
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      id={`answer-${idx}-${index}`}
                      value={answerOption}
                      checked={
                        Array.isArray(value) && value.includes(answerOption)
                      }
                      disabled={formIsReadOnly}
                      onChange={(e) => {
                        const newValue = e.target.checked
                          ? [...(value || []), answerOption]
                          : (value || []).filter((v) => v !== answerOption);
                        onChange(newValue);
                      }}
                      labelContent={
                        <div className="ml-2 text-caption">{answerOption}</div>
                      }
                    />
                  )}
                />
              </div>
            );
          })}
        </div>
      );

    case AssessmentQuestionType.Initials:
      question = question as InitialsAssessmentQuestion;

      const displayCompletedSignature =
        answer && isInitialsAssessmentAnswer(answer) && answer.signatureName;

      return (
        <div>
          <QuestionField question={question.question} />
          {displayCompletedSignature ? (
            <CompletedSignature
              signatureName={answer.signatureName}
              signatureRole={'Client'}
              signedAt={answer.signedAt}
              className="my-2"
            />
          ) : (
            <div className="flex flex-col items-start justify-start">
              <div className="mb-2 text-[11px] text-neutral-125">
                Awaiting client initials
              </div>
              <InputGroup
                IconLeft={SignatureFlagName}
                iconLeftClassName="h-5 w-5 -ml-1"
                containerClassName="w-[120px]"
                className="cursor-default rounded-lg"
                inputSize="extra-small"
                disabled
              />
            </div>
          )}
        </div>
      );

    case AssessmentQuestionType.Signature:
      question = question as SignatureAssessmentQuestion;
      return (
        <div>
          <QuestionField question={question.question} />

          {answer && isSignatureAssessmentAnswer(answer) && (
            <CompletedSignature
              signatureName={answer.signatureName}
              signatureRole={'Client'}
              signedAt={answer.signedAt}
              className="my-2"
            />
          )}
          {!answer && (
            <>
              <div className="mb-2 text-[11px] text-neutral-125">
                Awaiting client signature
              </div>
              <InputGroup
                placeholder="Client name"
                IconLeft={SignatureFlagName}
                containerClassName="mb-2 w-[398px]"
                className="rounded-lg pl-12"
                disabled
              />
            </>
          )}
        </div>
      );

    case AssessmentQuestionType.MultiSignature:
      question = question as MultiSignatureAssessmentQuestion;

      return (
        <div>
          <QuestionField question={question.question} />
          <MultiSignatureSection
            assessmentSignatureOrderIndices={
              question.assessmentSignatureOrderIndices
            }
            programAssessmentSignatureUsers={programAssessmentSignatureUsers}
            currentSigner={currentSigner}
            signatureTermsAgreed={signatureTermsAgreed}
            signatureRole={signatureRole}
            signatureName={signatureName}
            nowFormattedTimestamp={nowFormattedTimestamp}
          />
        </div>
      );

    case AssessmentQuestionType.ProviderSignature:
      question = question as ProviderSignatureAssessmentQuestion;
      return (
        <div>
          <QuestionField question={question.question} />
          {signatureName && signatureRole ? (
            <CompletedSignature
              signatureName={signatureName}
              signatureRole={signatureRole}
              className="my-2"
            />
          ) : (
            <p className="mt-2 text-caption text-neutral-200">
              Enter your name and role above to sign.
            </p>
          )}
        </div>
      );

    case AssessmentQuestionType.FileUpload: {
      const fileUploadQuestion = question as FileUploadAssessmentQuestion;
      return (
        <div>
          <QuestionField question={fileUploadQuestion.question} />
          <Controller
            name={`answers.${idx}` as any}
            control={control}
            rules={{
              required: fileUploadQuestion.isRequired
                ? 'Please upload a file'
                : false,
              validate: (val) => {
                if (!fileUploadQuestion.isRequired) return true;
                const v = val as { mediaId?: string } | undefined;
                if (!v?.mediaId?.trim()) return 'Please upload a file';
                return true;
              },
            }}
            render={({ field }) => (
              <ProviderFileUploadForAssessment
                programId={programId ?? ''}
                questionIndex={idx}
                question={fileUploadQuestion.question}
                isRequired={fileUploadQuestion.isRequired ?? false}
                value={
                  field.value?.mediaId && field.value?.fileName
                    ? {
                        mediaId: field.value.mediaId,
                        fileName: field.value.fileName,
                      }
                    : null
                }
                onChange={(value) => field.onChange(value ?? null)}
                disabled={formIsReadOnly}
                allowedExtensions={
                  fileUploadQuestion.allowedExtensions ?? undefined
                }
              />
            )}
          />
        </div>
      );
    }

    case AssessmentQuestionType.Autofill:
      if (isAutofillAssessmentQuestion(question)) {
        return (
          <AutofillQuestion
            question={question}
            answer={answer}
            idx={idx}
            register={register}
            control={control}
            setValue={setValue}
            isReadOnly={formIsReadOnly}
            programAssessmentSignatureUsers={programAssessmentSignatureUsers}
            fieldPrefix={fieldPrefix}
          />
        );
      }
      break;

    case AssessmentQuestionType.DatePicker:
      if (isDatePickerAssessmentQuestion(question)) {
        return (
          <DatePickerQuestion
            question={question}
            answer={answer}
            idx={idx}
            register={register}
            control={control}
            setValue={setValue}
            fieldPrefix={fieldPrefix}
          />
        );
      }
      break;

    case AssessmentQuestionType.PatientContact:
      if (isPatientContactAssessmentQuestion(question)) {
        const pcAnswer = answer as PatientContactAssessmentAnswer | undefined;
        const snapshot = pcAnswer?.contactSnapshot;
        if (snapshot) {
          const text = formatPatientContactSnapshotAnswer(snapshot);
          return (
            <div>
              <QuestionField question={question.question} />
              <div
                className="prose prose-sm max-w-none whitespace-pre-line font-medium text-green-150"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(text) }}
              />
            </div>
          );
        }
        return (
          <div className="text-caption text-neutral-125">
            Client Contact questions are completed by the client in their
            portal.
          </div>
        );
      }
      break;
  }

  return <></>;
};

export default Question;
